import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Lock, Sparkles } from 'lucide-react';
import { CoinIcon } from '@/components/coins';
import { quizzesService } from '@/services/quizzes.service';
import { ApiError } from '@/services/http';
import { usePaywall } from '@/hooks/usePaywall';
import type { QuizView } from '@/types/api';

/**
 * QuizCtaPanel — renders the "Take the quiz" entry-point panel at the
 * bottom of an article or inside the book detail modal. Fetches the
 * live quiz for the given parent; renders nothing when no live quiz
 * exists (the component self-gates so callers can drop it in
 * unconditionally).
 *
 * Matches the quiz spec's entry-point design language: soft teal card,
 * badge teaser, single primary CTA. Clicking routes to `/quiz/:id` on
 * the reader flow.
 */
export interface QuizCtaPanelProps {
  parentKind: 'book' | 'article';
  /** Book id for `book` kind, article slug for `article` kind. */
  parentKey: string;
}

export function QuizCtaPanel({ parentKind, parentKey }: QuizCtaPanelProps) {
  const navigate = useNavigate();
  const { isPaywalled, openPaywall } = usePaywall();
  const [quiz, setQuiz] = useState<QuizView | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const row =
          parentKind === 'book'
            ? await quizzesService.getForBook(parentKey)
            : await quizzesService.getForArticle(parentKey);
        if (!cancelled) setQuiz(row);
      } catch (err) {
        if (cancelled) return;
        // 404 / network — silently hide the CTA. The quiz is optional
        // content; never block the parent on a failed fetch.
        if (!(err instanceof ApiError)) {
          console.warn('Quiz CTA fetch failed:', err);
        }
        setQuiz(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [parentKind, parentKey]);

  if (loading || !quiz) return null;

  return (
    <section
      className="mt-10"
      style={{
        backgroundColor: '#EEF6F5',
        border: '1px solid #E3F1EF',
        borderRadius: '22px',
        padding: '22px 28px',
        display: 'flex',
        alignItems: 'center',
        gap: '24px',
        flexWrap: 'wrap',
      }}
    >
      <div
        style={{
          width: '56px',
          height: '56px',
          borderRadius: '999px',
          backgroundColor: '#FFFFFF',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
        aria-hidden
      >
        <CoinIcon size={40} inert />
      </div>
      <div className="flex-1" style={{ minWidth: '220px' }}>
        <div
          style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '11px',
            fontWeight: 600,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: '#1F6A6A',
          }}
        >
          Test yourself
        </div>
        <h2
          style={{
            fontFamily: '"DM Serif Display", serif',
            fontWeight: 400,
            fontSize: '22px',
            lineHeight: 1.2,
            color: '#1F1B16',
            margin: '4px 0 2px',
          }}
        >
          {quiz.questions.length} question{quiz.questions.length === 1 ? '' : 's'} — earn{' '}
          <span style={{ color: '#1F6A6A' }}>
            {quiz.coinReward} coin{quiz.coinReward === 1 ? '' : 's'}
          </span>
        </h2>
        <p
          style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '13px',
            color: '#8A7F72',
            margin: 0,
          }}
        >
          Tap any answer — no timer, no penalty for being wrong.
        </p>
      </div>
      <button
        type="button"
        onClick={() => {
          if (isPaywalled) {
            openPaywall('quiz');
            return;
          }
          navigate(`/quiz/${quiz.id}`);
        }}
        aria-label={isPaywalled ? 'Take the quiz — locked, pick a plan to unlock' : 'Take the quiz'}
        className="inline-flex items-center gap-2 transition-colors"
        style={{
          backgroundColor: '#2E8A8A',
          color: '#FFFFFF',
          padding: '12px 22px',
          borderRadius: '999px',
          fontFamily: 'Inter, sans-serif',
          fontSize: '14px',
          fontWeight: 600,
          border: 'none',
          cursor: 'pointer',
          boxShadow: '0 1px 2px rgba(31, 27, 22, 0.04)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#1F6A6A';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#2E8A8A';
        }}
      >
        {isPaywalled ? (
          <Lock className="h-4 w-4" aria-hidden />
        ) : (
          <Sparkles className="h-4 w-4" aria-hidden />
        )}
        Take the quiz
        {!isPaywalled ? <ArrowRight className="h-4 w-4" aria-hidden /> : null}
      </button>
    </section>
  );
}
